import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { config } from './config';
import { useAuth } from './auth';
import { getMyUpload } from './uploads';
import { refreshUpload, publishUpload, deleteUpload, updateUpload, setThumbnail, setCaption, ApiError, EarlyDeletionFeeRisk } from './api';
import { getSharedVideo, unshareVideo } from './shared-library';
import { listMyPlaylists, addVideoToPlaylist, PlaylistError } from './playlists';
import { StatusBadge, CaptionStatusBadge, formatRelativeTime, ShareAndEmbedCard, EditableTitle, EditableDescription, Thumbnail } from './ui';
import { HlsPlayer } from './hls-player';
import { SearchablePicker } from './searchable-picker';
import { ThumbnailPicker } from './thumbnail-picker';
import { CaptionPicker } from './caption-picker';
import { UploadStatus } from './types';

const TERMINAL: ReadonlySet<UploadStatus> = new Set(['ready', 'failed']);

export function DetailPage() {
  const { uploadId } = useParams<{ uploadId: string }>();
  const { identityId } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [pickingThumbnail, setPickingThumbnail] = useState(false);
  const [pickingCaption, setPickingCaption] = useState(false);

  const query = useQuery({
    queryKey: ['upload', identityId, uploadId],
    enabled: !!identityId && !!uploadId,
    queryFn: () => getMyUpload(identityId!, uploadId!),
  });

  const sharedQuery = useQuery({
    queryKey: ['shared-video', uploadId],
    enabled: !!uploadId,
    queryFn: () => getSharedVideo(uploadId!),
  });

  const refreshMutation = useMutation({
    mutationFn: () => refreshUpload(uploadId!),
    onSuccess: () => query.refetch(),
  });

  const publishMutation = useMutation({
    mutationFn: () => publishUpload(uploadId!),
    onSuccess: () => sharedQuery.refetch(),
  });

  const unshareMutation = useMutation({
    mutationFn: () => unshareVideo(uploadId!),
    onSuccess: () => sharedQuery.refetch(),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUpload(uploadId!),
    onSuccess: () => navigate('/library'),
  });

  // Auto-refresh with backoff while this upload hasn't reached a terminal state. This is where a
  // user naturally lands and waits right after uploading — without this, status only ever
  // updates via the manual "Refresh" button or by visiting the library list page (which has its
  // own, separate polling loop).
  const status = query.data?.status;
  useEffect(() => {
    if (!status || TERMINAL.has(status)) return;
    let delay = 5_000;
    const maxDelay = 60_000;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        await refreshMutation.mutateAsync();
      } catch {
        // Swallow — the user can see stale state and refresh manually.
      }
      delay = Math.min(delay * 2, maxDelay);
      if (!cancelled) setTimeout(tick, delay);
    };
    const timer = setTimeout(tick, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // refreshMutation is stable across renders thanks to react-query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (query.isLoading) return <p className="muted">Loading…</p>;
  if (!query.data) {
    return (
      <div>
        <p className="error">Upload not found.</p>
        <Link to="/library">Back to library</Link>
      </div>
    );
  }
  const row = query.data;
  const watchUrl = `https://${config.spaDomain}/watch/${row.uploadId}`;
  const embedUrl = `https://${config.spaDomain}/embed/${row.uploadId}`;
  const isShared = !!sharedQuery.data;

  return (
    <div>
      <Link to="/library" className="muted">
        ← Back to library
      </Link>
      <EditableTitle
        title={row.filename}
        onSave={async (next) => {
          await updateUpload(row.uploadId, { filename: next });
          query.refetch();
        }}
      />
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <StatusBadge status={row.status} />{' '}
        <span className="muted">Updated {formatRelativeTime(row.updatedAt)}</span>{' '}
        <button
          className="secondary"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          style={{ marginLeft: 8 }}
        >
          Refresh
        </button>
      </div>

      <EditableDescription
        description={row.description}
        onSave={async (next) => {
          await updateUpload(row.uploadId, { description: next });
          query.refetch();
        }}
      />

      {row.status === 'ready' && row.hlsUrl ? (
        <>
          <HlsPlayer
            src={row.hlsUrl}
            poster={row.thumbnailUrl}
            captionUrl={row.captionStatus === 'ready' ? row.captionUrl : undefined}
          />
          {pickingThumbnail ? (
            <ThumbnailPicker
              subjectId={row.uploadId}
              hlsUrl={row.hlsUrl}
              onSave={async (url) => {
                await setThumbnail(row.uploadId, url);
                setPickingThumbnail(false);
                query.refetch();
              }}
              onCancel={() => setPickingThumbnail(false)}
            />
          ) : (
            <div className="card" style={{ marginTop: 'var(--space-4)' }}>
              <h2>Thumbnail</h2>
              <Thumbnail src={row.thumbnailUrl} alt={`${row.filename} thumbnail`} size="lg" />
              <div style={{ marginTop: 'var(--space-3)' }}>
                <button className="secondary" onClick={() => setPickingThumbnail(true)}>
                  Choose thumbnail
                </button>
              </div>
            </div>
          )}
          {pickingCaption ? (
            <CaptionPicker
              subjectId={row.uploadId}
              onSave={async (url) => {
                await setCaption(row.uploadId, url);
                setPickingCaption(false);
                query.refetch();
              }}
              onCancel={() => setPickingCaption(false)}
            />
          ) : (
            <div className="card" style={{ marginTop: 'var(--space-4)' }}>
              <h2>Closed captions</h2>
              <CaptionStatusBadge status={row.captionStatus} />
              <div style={{ marginTop: 'var(--space-3)' }}>
                <button className="secondary" onClick={() => setPickingCaption(true)}>
                  {row.captionStatus === 'ready' ? 'Replace captions' : 'Upload captions'}
                </button>
              </div>
            </div>
          )}
          <ShareAndEmbedCard watchUrl={watchUrl} embedUrl={embedUrl} width={row.width} height={row.height} />
          <PublishCard
            isShared={isShared}
            onPublish={() => publishMutation.mutate()}
            onUnshare={() => unshareMutation.mutate()}
            publishPending={publishMutation.isPending}
            unsharePending={unshareMutation.isPending}
            publishError={publishMutation.isError}
          />
          <AddToPlaylistCard videoId={row.uploadId} />
        </>
      ) : row.status === 'failed' ? (
        <div className="card">
          <h2 className="error">Processing failed</h2>
          {row.errorMessage && <p className="error">{row.errorMessage}</p>}
          {row.errorDetails && (
            <p>
              <a href={row.errorDetails} target="_blank" rel="noreferrer">
                View processing logs
              </a>
            </p>
          )}
        </div>
      ) : (
        <div className="card">
          <p className="muted">
            Your video is {row.status === 'uploading' ? 'still uploading' : 'still being processed'}.
            Check back in a few minutes.
          </p>
        </div>
      )}

      <DeleteCard
        isShared={isShared}
        pending={deleteMutation.isPending}
        error={deleteMutation.error}
        onDelete={() => deleteMutation.mutate()}
      />
    </div>
  );
}

function PublishCard({
  isShared,
  onPublish,
  onUnshare,
  publishPending,
  unsharePending,
  publishError,
}: {
  isShared: boolean;
  onPublish: () => void;
  onUnshare: () => void;
  publishPending: boolean;
  unsharePending: boolean;
  publishError: boolean;
}) {
  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Shared library</h2>
      <p className="muted">
        {isShared
          ? 'This video is published to the org-wide shared library, visible to everyone signed in.'
          : 'Publish this video to the org-wide shared library, visible to everyone signed in.'}
      </p>
      {isShared ? (
        <button className="secondary" onClick={onUnshare} disabled={unsharePending}>
          {unsharePending ? 'Unsharing…' : 'Unshare'}
        </button>
      ) : (
        <button onClick={onPublish} disabled={publishPending}>
          {publishPending ? 'Publishing…' : 'Publish to shared library'}
        </button>
      )}
      {publishError && <p className="error">Couldn't publish — try again.</p>}
    </div>
  );
}

function DeleteCard({
  isShared,
  pending,
  error,
  onDelete,
}: {
  isShared: boolean;
  pending: boolean;
  error: Error | null;
  onDelete: () => void;
}) {
  const feeRisk =
    error instanceof ApiError && error.message === 'early_deletion_fee_risk'
      ? (error.details as EarlyDeletionFeeRisk)
      : null;

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Delete this video</h2>
      {isShared ? (
        <p className="muted">Unshare this video first before deleting it.</p>
      ) : (
        <p className="muted">This removes it from your library and deletes the uploaded file.</p>
      )}
      <button
        className="secondary"
        disabled={isShared || pending}
        onClick={() => {
          if (confirm('Delete this video? This cannot be undone.')) onDelete();
        }}
      >
        {pending ? 'Deleting…' : 'Delete'}
      </button>
      {feeRisk ? (
        <p className="error">
          This video's source file has been archived to {feeRisk.storageClass.replace('_', ' ')}{' '}
          storage and can't be deleted yet without an early-deletion fee. It'll be safe to delete
          starting {new Date(feeRisk.eligibleAt).toLocaleDateString()}.
        </p>
      ) : (
        error && <p className="error">Couldn't delete — try again.</p>
      )}
    </div>
  );
}

function AddToPlaylistCard({ videoId }: { videoId: string }) {
  const { identityId } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  const playlists = useQuery({
    queryKey: ['playlists', 'mine', identityId],
    enabled: !!identityId,
    queryFn: () => listMyPlaylists(identityId!),
  });

  const mutation = useMutation({
    mutationFn: (playlistId: string) => {
      const playlist = playlists.data?.find((p) => p.playlistId === playlistId);
      if (!playlist) throw new Error('Playlist not found.');
      return addVideoToPlaylist(playlist, videoId);
    },
    onSuccess: () => {
      setError(null);
      setAdded(true);
      playlists.refetch();
    },
    onError: (e) => setError(e instanceof PlaylistError ? e.message : "Couldn't add to playlist."),
  });

  const options = playlists.data ?? [];
  if (!identityId || options.length === 0) return null;

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Add to a playlist</h2>
      <SearchablePicker
        options={options.map((p) => ({ id: p.playlistId, label: p.name }))}
        onSelect={(id) => mutation.mutate(id)}
        placeholder="Search your playlists…"
        disabled={mutation.isPending}
      />
      {added && !error && <p className="muted">Added ✓</p>}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
